import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Baustein-Hook — useCardPeek.
 * Erkennt für `ui/CardImage` im Modus `landscape="peek"`, ob eine Maus auf einem Kartenbild
 * ruht (Spec docs/superpowers/specs/2026-10-31-querformat-karten-drehen-design.md §6.1).
 *
 *   const imageRef = useRef(null);
 *   const { peek, handlers } = useCardPeek({ imageRef });
 *   <div {...handlers}>
 *     <div ref={imageRef}><CardImage landscape="peek" peek={peek} orientation={…} /></div>
 *   </div>
 *
 * `peek` ist `{ hovering, resting }`:
 *   - `hovering`: eine Maus ist irgendwo über dem Wrapper, auch über Bedienelementen.
 *   - `resting`: die Maus ruht seit `PEEK_DELAY_MS` auf dem Bild, nicht auf einem Bedienelement,
 *     ohne Druck seit dem Eintritt. „Ruht" heißt: jede Bewegung startet die Verweilzeit neu,
 *     sonst erschiene das gedrehte Bild beim langsamen Überfahren einer breiten Kachel.
 *
 * Die drei Regeln, damit Bedienelemente auf der Karte klickbar bleiben: das gedrehte Bild
 * fängt keine Klicks (CardImage); liegt der Zeiger auf einem Element mit `data-card-control`
 * (Umdreh-/Dreh-Knopf, `CardOverlays.Button`, nicht-passive Badges), endet `resting` sofort;
 * die Verweilzeit läuft nur, während der Zeiger auf dem Bild selbst ruht.
 *
 * Der Hook liegt beim Konsumenten, nicht in CardImage: in `ui/CardTile` treffen die
 * Zeiger-Ereignisse das gestreckte Klick-Ziel der Kachel, das Kartenbild hat
 * `pointer-events: none`. Dieses Klick-Ziel deckt auch das Info-Feld unter dem Bild, deshalb
 * entscheidet das Rechteck von `imageRef`, nicht das Ereignis-Ziel, ob der Zeiger auf dem Bild
 * ist; ohne `imageRef` zählt der ganze Wrapper als Bild (Stapel- und Reihenansicht).
 *
 * Nur `pointerType === 'mouse'`: Touch und Stift auf Hybrid-Geräten bleiben außen vor. Der
 * Zeiger-Druck ist die einzige Zug-Sperre: ein dnd-kit-Zug beginnt mit genau diesem Druck,
 * danach liegt das Drag-Overlay unter dem Zeiger, und ein HTML-Drag erzeugt während des Zugs
 * keine Zeiger-Ereignisse. Scrollen, Fenstergröße und jede Taste (ein Kürzel, das ein Modal
 * öffnet — Radix schaltet den Zeiger dann ab, ohne `pointerleave`) beenden `resting`; diese
 * Fenster-Listener stehen nur, solange `hovering` wahr ist, in Sammlung und Stapelansicht
 * also für höchstens eine von Dutzenden Karten.
 *
 * `handlers` ist referenzstabil, `peek` ein neues Objekt nur bei einer Änderung.
 */
export const PEEK_DELAY_MS = 200;

const IDLE = { hovering: false, resting: false };

function insideRect(el, x, y) {
  if (!el) return true;
  const r = el.getBoundingClientRect();
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

export function useCardPeek({ imageRef } = {}) {
  const [peek, setPeek] = useState(IDLE);
  const timerRef = useRef(null);
  const pressedRef = useRef(false);
  const restingRef = useRef(false);

  const cancelTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const setResting = useCallback((resting) => {
    restingRef.current = resting;
    setPeek((p) => (p.resting === resting ? p : { ...p, resting }));
  }, []);

  const stopResting = useCallback(() => {
    cancelTimer();
    setResting(false);
  }, [cancelTimer, setResting]);

  const handlers = useMemo(() => {
    const track = (e) => {
      if (e.pointerType !== 'mouse') return;
      setPeek((p) => (p.hovering ? p : { ...p, hovering: true }));
      const overControl = !!(e.target && typeof e.target.closest === 'function' && e.target.closest('[data-card-control]'));
      const inside = insideRect(imageRef?.current, e.clientX, e.clientY);
      if (overControl || pressedRef.current || !inside) {
        stopResting();
        return;
      }
      if (restingRef.current) return;
      cancelTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setResting(true);
      }, PEEK_DELAY_MS);
    };
    return {
      onPointerEnter: (e) => { pressedRef.current = false; track(e); },
      onPointerMove: track,
      onPointerLeave: () => {
        cancelTimer();
        pressedRef.current = false;
        restingRef.current = false;
        setPeek((p) => (p.hovering || p.resting ? IDLE : p));
      },
      onPointerDown: () => {
        pressedRef.current = true;
        stopResting();
      },
    };
  }, [imageRef, cancelTimer, setResting, stopResting]);

  useEffect(() => {
    if (!peek.hovering) return undefined;
    const end = () => stopResting();
    window.addEventListener('scroll', end, { capture: true, passive: true });
    window.addEventListener('resize', end);
    window.addEventListener('keydown', end);
    return () => {
      window.removeEventListener('scroll', end, { capture: true });
      window.removeEventListener('resize', end);
      window.removeEventListener('keydown', end);
    };
  }, [peek.hovering, stopResting]);

  useEffect(() => cancelTimer, [cancelTimer]);

  return { peek, handlers };
}

export default useCardPeek;
